import { useRef, useState } from "react";
import { ApiError, post, upload } from "../api";
import type { SourceRecord, SourcesPayload } from "../api";
import { Loading, Refused } from "../components/Shell";
import { useApi } from "../useApi";

/**
 * What the engine reads, and the two things a person may do to it.
 *
 * Everything Neev knows arrives as files a timer re-reads every fifteen
 * minutes. That was true before this screen existed and invisible from
 * the application: adding a document meant an scp, and nothing on screen
 * said whether the last poll had picked anything up.
 */
export function Sources() {
  const { data, error, loading, reload } = useApi<SourcesPayload>("/sources");
  const [busy, setBusy] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [said, setSaid] = useState<string | null>(null);

  if (loading) return <Loading />;
  if (error) return <Refused status={error.status} detail={error.detail} />;
  if (!data) return null;

  async function refresh() {
    setBusy("refresh"); setProblem(null); setSaid(null);
    try {
      const out = await post<{ events: number; clocks: number; unreadable?: string[] }>("/sources/refresh");
      const unread = out.unreadable ?? [];
      setSaid(
        `Read the sources and re-evaluated ${out.clocks} clocks across ${out.events} events.` +
        (unread.length
          ? ` ${unread.length} file${unread.length === 1 ? "" : "s"} could not be used: ${unread.join("; ")}.`
          : ""));
      reload();
    } catch (e) {
      setProblem(e instanceof ApiError ? e.detail : String(e));
    } finally { setBusy(null); }
  }

  async function send(source: SourceRecord, file: File) {
    setBusy(source.key); setProblem(null); setSaid(null);
    try {
      await upload(`/sources/${encodeURIComponent(source.key)}/files`, file);
      setSaid(`${file.name} is in ${source.key}. It is read at the next poll, or press Read the sources now.`);
      reload();
    } catch (e) {
      setProblem(e instanceof ApiError ? e.detail : String(e));
    } finally { setBusy(null); }
  }

  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 24,
                    alignItems: "flex-start", flexWrap: "wrap" }}>
        <div>
          <h1>Sources</h1>
          <p className="standfirst">
            Everything Neev knows is read from these folders. They are polled
            every {data.poll_minutes} minutes; nothing here is read from a
            person typing into the application.
          </p>
        </div>
        {data.may_refresh && (
          <div style={{ textAlign: "right", flexShrink: 0 }}>
            <button className="primary" disabled={busy !== null} onClick={refresh}>
              {busy === "refresh" ? "Reading…" : "Read the sources now"}
            </button>
            <div className="hint" style={{ marginTop: 6 }}>
              Last read {data.last_read_at ? data.last_read_at.slice(0, 16).replace("T", " ") : "never"}
            </div>
          </div>
        )}
      </div>

      {said && <div className="note"><span className="h">Done</span>{said}</div>}
      {problem && <div className="err">{problem}</div>}

      {!data.may_supply && (
        <div className="note">
          <span className="h">You can see these, but not add to them</span>
          Supplying a file changes what every clock is computed from — a new
          register export can move a deadline or extinguish a right — so it
          needs the contract authority role.
        </div>
      )}

      {data.sources.map((s) => (
        <SourceCard key={s.key} source={s} maxBytes={data.max_upload_bytes}
          maySupply={data.may_supply} busy={busy === s.key}
          onSend={(file) => send(s, file)} />
      ))}
    </>
  );
}

function SourceCard({ source, maxBytes, maySupply, busy, onSend }: {
  source: SourceRecord; maxBytes: number; maySupply: boolean;
  busy: boolean; onSend: (file: File) => void;
}) {
  const picker = useRef<HTMLInputElement>(null);
  const remote = source.reading_from !== "local";

  return (
    <section className="card">
      <div style={{ display: "flex", justifyContent: "space-between", gap: 18,
                    alignItems: "flex-start", flexWrap: "wrap" }}>
        <div style={{ minWidth: 0 }}>
          <h2 className="secttl" style={{ margin: 0 }}>{source.key}</h2>
          <p className="hint" style={{ maxWidth: "62ch" }}>{source.description}</p>
        </div>
        <div style={{ textAlign: "right", flexShrink: 0 }}>
          {remote ? (
            /* A folder next to a Graph or API adapter is not where that
               source comes from, so no upload is offered for it. */
            <span className="pill act">read from {source.reading_from}</span>
          ) : maySupply ? (
            <>
              <input ref={picker} type="file" style={{ display: "none" }}
                accept={source.accepts.join(",")}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) onSend(file);
                  e.target.value = "";
                }} />
              <button className="btn" disabled={busy}
                onClick={() => picker.current?.click()}>
                {busy ? "Sending…" : "Add a file"}
              </button>
            </>
          ) : null}
          <div className="hint" style={{ marginTop: 6 }}>
            {source.accepts.length ? source.accepts.join(" · ") : "any file"}
            {" · up to "}{Math.round(maxBytes / (1024 * 1024))}MB
          </div>
        </div>
      </div>

      {source.files.length === 0 ? (
        <p className="empty">
          {remote
            ? "Read from elsewhere, so there is nothing in a folder here."
            : "Nothing here yet. Nothing from this source reaches the engine until there is."}
        </p>
      ) : (
        <table>
          <thead><tr><th>File</th><th className="num">Size</th><th className="num">Changed</th></tr></thead>
          <tbody>
            {source.files.map((f) => (
              <tr key={f.name}>
                <td className="mn">{f.name}</td>
                <td className="num mn">{size(f.bytes)}</td>
                <td className="num mn withheld">
                  {f.modified ? f.modified.slice(0, 16).replace("T", " ") : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function size(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
